import React from 'react';
import { Pressable, View } from 'react-native';
import { Feather } from '@expo/vector-icons';

import Text from '../../components/text/Text';
import { useTheme } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';

/**
 * TickRow: an item to tick off (keys, money bags, return stock, pick list rows).
 * Tick box on the RIGHT (SPEC decision 13). Ticked rows turn quiet.
 * Props:
 * - title, subtitle, overline (small caps label, e.g. "KEY / FOB")
 * - value: short text between the body and the tick box (e.g. a quantity "x7")
 * - checked (bool), onToggle(nextChecked)
 * - disabled, accessibilityHint, style
 * - quietWhenChecked: false keeps ticked rows at full strength where ticked means "on" (e.g. Home tools)
 * - children: optional extra content under the subtitle (e.g. a product image row)
 */
const TickRow = ({
	title,
	subtitle,
	overline,
	value,
	checked = false,
	onToggle,
	disabled = false,
	quietWhenChecked = true,
	accessibilityHint,
	style,
	children,
}) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const isQuiet = checked && quietWhenChecked;

	return (
		<Pressable
			onPress={disabled || !onToggle ? undefined : () => onToggle(!checked)}
			disabled={disabled || !onToggle}
			accessibilityRole="checkbox"
			accessibilityLabel={[overline, title, value, subtitle].filter(Boolean).join(', ')}
			accessibilityHint={accessibilityHint}
			accessibilityState={{ checked, disabled }}
			style={({ pressed }) => [styles.row, isQuiet && styles.rowChecked, pressed && styles.pressed, style]}
		>
			<View style={styles.body}>
				{Boolean(overline) && <Text style={styles.overline}>{overline}</Text>}
				<Text style={[styles.title, isQuiet && styles.titleChecked]}>{title}</Text>
				{Boolean(subtitle) && <Text style={styles.subtitle}>{subtitle}</Text>}
				{children}
			</View>
			{Boolean(value) && <Text style={styles.value}>{value}</Text>}
			<View style={[styles.box, checked && styles.boxChecked, disabled && styles.boxDisabled]}>
				{checked && <Feather name="check" size={20} color={colors.onPrimary} />}
			</View>
		</Pressable>
	);
};

const createStyles = ({ ui: { colors, radius, type, space } }) => ({
	row: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.md,
		minHeight: 64,
		paddingHorizontal: 14,
		paddingVertical: 10,
		borderRadius: radius.card,
		borderWidth: 1,
		borderColor: colors.raisedBorder,
		backgroundColor: colors.raised,
	},
	rowChecked: {
		borderColor: colors.card,
		backgroundColor: colors.card,
	},
	pressed: {
		opacity: 0.85,
	},
	body: {
		flex: 1,
		gap: 2,
	},
	overline: {
		...type.overline,
		letterSpacing: 0.48,
		color: colors.textSecondary,
	},
	title: {
		...type.rowTitle,
		color: colors.text,
	},
	titleChecked: {
		color: colors.textSecondary,
	},
	subtitle: {
		...type.caption,
		color: colors.textSecondary,
	},
	value: {
		...type.bodyStrong,
		color: colors.text,
		minWidth: 40,
		textAlign: 'right',
	},
	box: {
		width: 32,
		height: 32,
		borderRadius: radius.pill,
		borderWidth: 2,
		borderColor: colors.iconMuted,
		alignItems: 'center',
		justifyContent: 'center',
	},
	boxChecked: {
		borderColor: colors.primary,
		backgroundColor: colors.primary,
	},
	boxDisabled: {
		borderColor: colors.borderStrong,
	},
});

export default TickRow;
